const DEFAULT_BASE_URL = "https://alentours.ca";
const DEFAULT_TODAY_TIMEZONE = "America/Toronto";

function stripTags(value = "") {
    return value.replace(/<[^>]*>/g, " ");
}

function collapseWhitespace(value = "") {
    return value.replace(/\s+/g, " ").trim();
}

function truncateText(value = "", maxLength = 160) {
    if (value.length <= maxLength) {
        return value;
    }

    const sliced = value.slice(0, maxLength + 1);
    const lastSentence = Math.max(sliced.lastIndexOf(". "), sliced.lastIndexOf("! "), sliced.lastIndexOf("? "));
    if (lastSentence >= Math.floor(maxLength * 0.6)) {
        return sliced.slice(0, lastSentence + 1).trim();
    }

    const lastSpace = sliced.lastIndexOf(" ");
    if (lastSpace > 0) {
        return `${sliced.slice(0, lastSpace).trim()}...`;
    }

    return `${value.slice(0, maxLength).trim()}...`;
}

function normalizeText(value = "") {
    return value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();
}

function capitalizeFirstLetter(value = "") {
    if (!value) {
        return "";
    }

    return value.charAt(0).toUpperCase() + value.slice(1);
}

function formatFrenchDate(dateInput, options = {}) {
    const {
        timeZone = DEFAULT_TODAY_TIMEZONE,
        includeWeekday = true
    } = options;

    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return capitalizeFirstLetter(new Intl.DateTimeFormat("fr-CA", {
        timeZone,
        ...(includeWeekday ? { weekday: "long" } : {}),
        year: "numeric",
        month: "long",
        day: "numeric"
    }).format(date));
}

function formatYmdInTimeZone(dateInput, timeZone = DEFAULT_TODAY_TIMEZONE) {
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit"
    }).formatToParts(date);

    const map = Object.fromEntries(parts.map(part => [part.type, part.value]));
    return `${map.year}-${map.month}-${map.day}`;
}

function getHourInTimeZone(dateString, timeZone = DEFAULT_TODAY_TIMEZONE) {
    if (!dateString) {
        return 12;
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
        return 12;
    }

    const hour = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        hour: "2-digit",
        hour12: false
    }).format(date);

    const parsedHour = Number.parseInt(hour, 10);
    return Number.isFinite(parsedHour) ? parsedHour : 12;
}

function includesNormalized(haystack = "", needle = "") {
    const normalizedNeedle = normalizeText(needle);
    if (!normalizedNeedle) {
        return false;
    }

    return normalizeText(haystack).includes(normalizedNeedle);
}

function formatEventDateLong(dateString, timeUnkown = false) {
    if (!dateString) {
        return "";
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
        return "";
    }

    return new Intl.DateTimeFormat("fr-CA", {
        timeZone: "America/Toronto",
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        ...(timeUnkown ? {} : { hour: "numeric", minute: "2-digit" })
    }).format(date);
}

export function getEventPriceLabel(event) {
    const price = String(event?.price ?? "").trim();
    if (!price) {
        return "";
    }

    if (price === "0") {
        return "Gratuit";
    }

    return price;
}

export function getEventShortDescription(event, maxLength = 110) {
    const description = collapseWhitespace(stripTags(event?.description ?? ""));
    if (!description) {
        return "";
    }

    return truncateText(description, maxLength);
}

export function getEventDateLabel(event) {
    const dateText = event?.date?.trim() ?? "";

    if (dateText && dateText.toLowerCase().includes("du")) {
        return dateText;
    }

    if (Array.isArray(event?.dates) && event.dates.length > 1) {
        return "Plusieurs dates";
    }

    if (Array.isArray(event?.dates) && event.dates.length === 1) {
        return formatEventDateLong(event.dates[0]?.date, event.timeUnkown);
    }

    return dateText;
}

export function buildEventSeo(event, options = {}) {
    const baseUrl = (options.baseUrl ?? DEFAULT_BASE_URL).replace(/\/$/, "");
    const eventTitle = event?.title?.trim() ?? "";
    const city = event?.city?.trim() ?? "";
    const venue = event?.locationName?.trim() ?? "";
    const dateLabel = getEventDateLabel(event);
    const priceLabel = getEventPriceLabel(event);
    const shortDescription = getEventShortDescription(event);

    const titleContainsCity = includesNormalized(eventTitle, city);
    const titleContainsVenue = includesNormalized(eventTitle, venue);

    let pageTitle = eventTitle;

    if (city && !titleContainsCity) {
        pageTitle += ` à ${city}`;
    }

    if (venue && !titleContainsVenue) {
        pageTitle += ` - ${venue}`;
    }

    if (dateLabel) {
        pageTitle += ` - ${dateLabel}`;
    }

    const attendanceParts = [eventTitle];
    if (venue) {
        attendanceParts.push(venue);
    }
    if (city && !titleContainsCity) {
        attendanceParts.push(city);
    }
    if (dateLabel) {
        attendanceParts.push(dateLabel);
    }

    const metaSegments = [`${attendanceParts.join(", ")}.`];
    if (priceLabel) {
        metaSegments.push(`${priceLabel}.`);
    }
    if (shortDescription) {
        metaSegments.push(shortDescription);
    }
    metaSegments.push("...Plus sur alentours.");

    const metaDescription = truncateText(metaSegments.join(" "), 180);

    return {
        h1Title: eventTitle,
        structuredDataName: eventTitle,
        pageTitle,
        metaDescription,
        canonicalUrl: `${baseUrl}/details/${event.id}`,
        priceLabel,
        shortDescription,
        dateLabel
    };
}

export function organizeTodayEvents(events = [], options = {}) {
    const timeZone = options.timeZone ?? DEFAULT_TODAY_TIMEZONE;
    const organizedByTime = { morning: [], afternoon: [], evening: [], anyTime: [] };

    events.forEach((event) => {
        if (!event?.timeUnkown && Array.isArray(event?.dates) && event.dates[0]?.date) {
            const hour = getHourInTimeZone(event.dates[0].date, timeZone);

            if (hour < 12) {
                organizedByTime.morning.push(event);
                return;
            }

            if (hour < 17) {
                organizedByTime.afternoon.push(event);
                return;
            }

            organizedByTime.evening.push(event);
            return;
        }

        organizedByTime.anyTime.push(event);
    });

    return organizedByTime;
}

export function buildTodayPageSeo(currentDate = new Date(), events = [], options = {}) {
    const timeZone = options.timeZone ?? DEFAULT_TODAY_TIMEZONE;
    const formattedDate = formatFrenchDate(currentDate, { timeZone, includeWeekday: true });
    const shortDate = formatFrenchDate(currentDate, { timeZone, includeWeekday: false });
    const eventCountLabel = events.length > 0 ? `${events.length} idées de sorties sélectionnées aujourd'hui.` : "La sélection du jour est mise à jour quotidiennement.";

    return {
        title: `Quoi faire à Québec aujourd'hui (${shortDate}) | Événements, activités et sorties`,
        metaDescription: `Quoi faire à Québec aujourd’hui (${shortDate}) : événements, activités, sorties culturelles, idées gratuites et quoi faire ce soir à Québec. Sélection mise à jour chaque jour.`,
        h1: "Quoi faire à Québec aujourd'hui",
        dateLabel: `Aujourd'hui, ${formattedDate}`,
        intro: `Tu cherches quoi faire à Québec aujourd'hui? Retrouve sur cette page les événements, activités et sorties à faire ce matin, cet après-midi, et ce soir partout dans la ville. ${eventCountLabel}`,
        jumpLinks: [
            { id: "matin", label: "Ce matin" },
            { id: "apres-midi", label: "Cet après-midi" },
            { id: "ce-soir", label: "Ce soir" },
            { id: "toute-heure", label: "À toute heure" }
        ],
        sections: [
            {
                key: "morning",
                id: "matin",
                title: "Que faire ce matin à Québec",
                emptyText: "Aucun événement avec horaire matinal n'est affiché pour le moment. Tu peux regarder les idées à toute heure ou explorer la recherche complète pour trouver d'autres sorties à Québec aujourd'hui."
            },
            {
                key: "afternoon",
                id: "apres-midi",
                title: "Que faire cet après-midi à Québec",
                emptyText: "Aucune activité d'après-midi n'est affichée pour l'instant. Consulte les événements à toute heure ou ouvre la recherche complète pour trouver d'autres idées de sorties à Québec aujourd'hui."
            },
            {
                key: "evening",
                id: "ce-soir",
                title: "Que faire ce soir à Québec",
                emptyText: "Aucune sortie du soir n'est affichée pour le moment. Reviens plus tard ou parcoure tous les événements à Québec aujourd'hui pour trouver une idée de dernière minute."
            },
            {
                key: "anyTime",
                id: "toute-heure",
                title: "Activités à Québec aujourd'hui à toute heure",
                emptyText: "Aucune activité sans horaire précis n'est affichée pour l'instant. Consulte les autres sections ou lance une recherche plus large parmi tous les événements à Québec."
            }
        ],
        itemListLabel: `Quoi faire à Québec aujourd'hui le ${shortDate}`
    };
}

export function isEventOnDate(event, dateInput, options = {}) {
    const timeZone = options.timeZone ?? DEFAULT_TODAY_TIMEZONE;
    const targetYmd = formatYmdInTimeZone(dateInput, timeZone);

    if (!targetYmd || !Array.isArray(event?.dates)) {
        return false;
    }

    return event.dates.some((dateEntry) => formatYmdInTimeZone(dateEntry?.date, timeZone) === targetYmd);
}

function dedupeEventsById(events = []) {
    const seen = new Set();
    return events.filter((event) => {
        if (!event?.id || seen.has(event.id)) {
            return false;
        }

        seen.add(event.id);
        return true;
    });
}

function dedupeAndLimitEvents(events = [], limit = 12) {
    return dedupeEventsById(events).slice(0, limit);
}

export function buildFreeActivitiesSections(sectionEvents = {}) {
    return {
        today: dedupeAndLimitEvents(sectionEvents.today ?? []),
        weekend: dedupeAndLimitEvents(sectionEvents.weekend ?? []),
        family: dedupeAndLimitEvents(sectionEvents.family ?? []),
        exhibitions: dedupeAndLimitEvents(sectionEvents.exhibitions ?? []),
        shows: dedupeAndLimitEvents(sectionEvents.shows ?? [])
    };
}

export function buildFreeActivitiesPageSeo() {
    return {
        title: "Activités gratuites à Québec | Quoi faire gratuitement aujourd'hui et ce week-end",
        metaDescription: "Activités gratuites à Québec : idées de sorties gratuites aujourd'hui, ce week-end, en famille, dans les expositions et les spectacles. Sélection mise à jour en continu.",
        h1: "Activités gratuites à Québec",
        introParagraphs: [
            "Tu cherches des activités gratuites à Québec sans perdre de temps à fouiller partout? Cette page regroupe des idées de sorties gratuites pour aujourd'hui, ce week-end et les prochains jours. Tu y trouveras des événements culturels, des expositions, des spectacles, des activités familiales et des occasions de sortir à Québec sans billet payant.",
            "L'objectif n'est pas seulement d'afficher une suite de cartes. On veut aussi t'aider à repérer rapidement le bon type d'activité gratuite selon tes intérêts. Si tu cherches une idée pour aujourd'hui, tu peux commencer par la section du jour. Si tu planifies vos sorties d'avance, les sections dédiées au week-end, à la famille, aux expositions et aux spectacles gratuits te donnent une vue plus utile.",
            "Les événements affichés ici sont sélectionnés à partir des activités publiées sur alentours avec un prix gratuit. Cela peut inclure des concerts sans frais, des expositions à entrée libre, des activités culturelles ouvertes à tous, des festivals, des animations de quartier ou des idées de sorties en famille accessibles sans dépenser. La page est mise à jour régulièrement pour rester utile à la recherche locale."
        ],
        sections: [
            {
                key: "today",
                id: "aujourdhui",
                title: "Activités gratuites à Québec aujourd'hui",
                intro: "Une sélection rapide pour trouver quoi faire gratuitement à Québec aujourd'hui, que tu cherches une sortie culturelle, une activité de quartier ou une idée de dernière minute.",
                emptyText: "Aucune activité gratuite n'est affichée pour aujourd'hui en ce moment. Tu peux consulter les sections du week-end, en famille, expositions ou spectacles pour trouver d'autres idées gratuites à Québec."
            },
            {
                key: "weekend",
                id: "weekend",
                title: "Activités gratuites à Québec ce week-end",
                intro: "Cette section met en avant les sorties gratuites à surveiller du vendredi au dimanche pour préparer ton week-end à Québec sans dépenser.",
                emptyText: "Aucune activité gratuite n'est encore affichée pour ce week-end. Reviens plus tard ou regarde les autres sections pour trouver des sorties gratuites déjà publiées à Québec."
            },
            {
                key: "family",
                id: "famille",
                title: "Activités gratuites en famille à Québec",
                intro: "Des idées accessibles pour sortir avec les enfants, profiter d'une activité familiale ou planifier une sortie gratuite à Québec pour tous les âges.",
                emptyText: "Aucune activité familiale gratuite n'est affichée pour le moment. Continue avec les expositions ou les spectacles gratuits pour trouver une autre idée de sortie à Québec."
            },
            {
                key: "exhibitions",
                id: "expositions",
                title: "Expositions gratuites à Québec",
                intro: "Pour celles et ceux qui cherchent une sortie culturelle gratuite, cette section rassemble les expositions et activités artistiques accessibles sans frais à Québec.",
                emptyText: "Aucune exposition gratuite n'est affichée pour le moment. Consulte les autres sections pour trouver une autre sortie culturelle gratuite à Québec."
            },
            {
                key: "shows",
                id: "spectacles",
                title: "Spectacles gratuits à Québec",
                intro: "Concerts, humour, théâtre ou autres performances sans frais: cette section aide à repérer les spectacles gratuits à Québec déjà annoncés sur la plateforme.",
                emptyText: "Aucun spectacle gratuit n'est affiché pour le moment. Tu peux continuer avec les activités d'aujourd'hui ou du week-end pour trouver une autre sortie gratuite à Québec."
            }
        ],
        bottomParagraphs: [
            "Les activités gratuites à Québec peuvent prendre plusieurs formes selon la saison et les organismes qui les proposent. On retrouve souvent des expositions à entrée libre, des spectacles en plein air, des activités pour les familles, des événements communautaires et des initiatives culturelles soutenues par la Ville de Québec ou par des partenaires locaux. En gardant cette page à jour, on t'aide à trouver plus vite les vraies occasions de sortir sans frais.",
            "Si tu veux élargir ta recherche, tu peux aussi ouvrir la recherche complète d'alentours, consulter les sorties d'aujourd'hui à Québec ou parcourir les activités familiales. Cela te permet de comparer les options gratuites avec les autres événements en ville et de trouver plus facilement quoi faire selon ton budget, ton quartier ou le type de sortie que tu préféres."
        ],
        itemListLabel: "Activités gratuites à Québec"
    };
}

export function buildFamilyActivitiesSections(sectionEvents = {}) {
    return {
        today: dedupeAndLimitEvents(sectionEvents.today ?? []),
        weekend: dedupeAndLimitEvents(sectionEvents.weekend ?? []),
        free: dedupeAndLimitEvents(sectionEvents.free ?? []),
        upcoming: dedupeAndLimitEvents(sectionEvents.upcoming ?? [])
    };
}

export function buildFamilyActivitiesPageSeo() {
    return {
        title: "Activités familiales à Québec | Sorties en famille aujourd'hui et ce week-end",
        metaDescription: "Activités familiales à Québec : sorties en famille aujourd'hui, ce week-end et activités gratuites en famille. Sélection mise à jour en continu.",
        h1: "Activités familiales à Québec",
        introParagraphs: [
            "Tu cherches une activité familiale à Québec sans devoir fouiller dans une liste trop générale? Cette page rassemble des idées de sorties en famille pour aujourd'hui, ce week-end et les prochains jours afin de t'aider à trouver plus vite une activité adaptée à ton moment.",
            "Tu peux t'en servir pour trouver une activité familiale de dernière minute, planifier une sortie pour le week-end ou repérer des événements gratuits en famille à Québec.",
            "Les activités affichées ici viennent des événements publiés sur alentours et classés dans la catégorie famille. Tu y retrouveras selon les disponibilités des ateliers, des spectacles, des expositions, des fêtes de quartier, des activités culturelles et d'autres sorties pensées pour profiter de Québec en famille."
        ],
        sections: [
            {
                key: "today",
                id: "aujourdhui",
                title: "Activités familiales à Québec aujourd'hui",
                intro: "Une sélection rapide pour trouver une idée de sortie en famille à Québec aujourd'hui, que tu veuilles occuper une partie de la journée ou improviser une activité de dernière minute.",
                emptyText: "Aucune activité familiale n'est affichée pour aujourd'hui en ce moment. Regarde les sections du week-end, des activités gratuites en famille ou les sorties à venir pour trouver une autre idée."
            },
            {
                key: "weekend",
                id: "weekend",
                title: "Activités familiales à Québec ce week-end",
                intro: "Cette section te donne un point de départ pour préparer ton week-end en famille à Québec avec des activités prévues du vendredi au dimanche.",
                emptyText: "Aucune activité familiale n'est encore affichée pour ce week-end. Reviens plus tard ou consulte les autres sections pour voir les sorties déjà annoncées."
            },
            {
                key: "free",
                id: "gratuites",
                title: "Activités gratuites en famille à Québec",
                intro: "Si tu veux sortir sans trop dépenser, cette section met en avant les activités familiales gratuites déjà publiées sur alentours.",
                emptyText: "Aucune activité gratuite en famille n'est affichée pour le moment. Continue avec les autres sections ou consulte la page dédiée aux activités gratuites à Québec."
            },
            {
                key: "upcoming",
                id: "a-venir",
                title: "Sorties en famille à Québec à venir",
                intro: "Pour t'aider à planifier un peu plus loin, cette section rassemble d'autres activités familiales à Québec prévues dans les prochains jours.",
                emptyText: "Aucune autre sortie en famille n'est affichée pour l'instant. Consulte la recherche complète pour voir tous les événements disponibles à Québec."
            }
        ],
        bottomParagraphs: [
            "Les sorties en famille à Québec peuvent changer vite selon la saison, les congés scolaires, la météo et les programmations culturelles. C'est pour ça qu'une page comme celle-ci est utile: elle te permet de repérer rapidement ce qui est pertinent pour aujourd'hui ou pour le week-end, sans devoir recommencer ta recherche chaque fois.",
            "Si tu veux élargir tes options, tu peux aussi consulter les activités gratuites à Québec, voir quoi faire aujourd'hui ou ouvrir la recherche complète d'alentours. Tu pourras ainsi comparer les sorties familiales avec les autres événements en ville et trouver plus facilement une activité qui convient à ton budget, à ton horaire et à ton quartier."
        ],
        itemListLabel: "Activités familiales à Québec"
    };
}

export function buildHomePageSeo() {
    return {
        title: "alentours – Tous les événements à Québec aujourd’hui, cette semaine et à venir",
        metaDescription: "Découvre les meilleurs événements et activités à Québec. Trouve quoi faire aujourd'hui, cette semaine ou ce mois-ci : musique, humour, théâtre, famille et plus.",
        ogTitle: "alentours – Tous les événements à Québec aujourd’hui, cette semaine et à venir",
        ogDescription: "Découvre les meilleurs événements et activités à Québec. Trouve quoi faire aujourd'hui, cette semaine ou ce mois-ci : musique, humour, théâtre, famille et plus."
    };
}

export function buildWeekPageSeo(periodLabel, eventCount = 0) {
    const countLabel = eventCount > 0 ? `${eventCount} idées de sorties déjà repérées.` : "Une sélection mise à jour chaque semaine.";

    return {
        title: `Événements à Québec cette semaine | Quoi faire du ${periodLabel}`,
        metaDescription: `Événements à Québec cette semaine : concerts, spectacles, expositions, sorties en famille et idées de dernière minute pour la semaine du ${periodLabel}.`,
        h1: "Événements et activités à Québec cette semaine",
        introParagraphs: [
            `Tu veux savoir quoi faire à Québec cette semaine sans ouvrir dix onglets? Cette page rassemble les événements et activités à surveiller pour la semaine du ${periodLabel}. ${countLabel}`,
            "Tu peux t'en servir pour planifier une sortie après le travail, préparer ton week-end à l'avance ou repérer des concerts, expositions, activités familiales et autres idées de sorties déjà annoncées à Québec."
        ],
        sectionTitle: `Quoi faire à Québec cette semaine (${periodLabel})`,
        bottomParagraphs: [
            "Si tu veux affiner davantage, tu peux ouvrir la recherche complète d'alentours ou passer par les autres pages thématiques pour trouver une sortie qui correspond à ton budget, à ton horaire ou à tes envies."
        ],
        itemListLabel: `Événements à Québec pour la semaine du ${periodLabel}`
    };
}
